import { useLocation, useNavigate } from "react-router-dom";
import useAuthentication from "../../../hooks/useAuthentication";
import { useForm } from "react-hook-form";
import {
  initialFormData,
  MasterFormDataType,
  MasterTableDataType,
} from "./types";
import usePopup from "../../../hooks/usePopup";
import { useMutation, useQuery } from "react-query";
import { getAllHPIComponents, saveMasterData } from "./services";
import { useEffect } from "react";
import {
  Paper,
  Typography,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import { CustomFormField } from "../../../utilis/component/CustomFormElement";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const pageTitle = "HPI ";
function AddOrEditHPI() {
  const { user } = useAuthentication();
  const navigate = useNavigate();
  const { control, handleSubmit, reset } = useForm<MasterFormDataType>({
    defaultValues: initialFormData,
  });
  const { data: hpiComponent, isLoading: hpiComponentLoading } = useQuery(
    [],
    getAllHPIComponents,
  );
  const {
    state: { data = null },
  } = useLocation() as { state: { data: MasterTableDataType } };
  const { showSnackbar } = usePopup();
  const handleGoBack = () => {
    navigate(-1);
  };
  const { mutate: save, isLoading } = useMutation(saveMasterData, {
    onSuccess: (result) => {
      if (result.responseIndicator == "success") {
        showSnackbar({
          message: result.responseMessage,
          open: true,
          variant: "success",
          duration: 3000,
        });
        handleGoBack();
      } else {
        showSnackbar({
          message: result.responseMessage,
          open: true,
          variant: "warning",
          duration: 3000,
        });
      }
    },
    onError: (e: any) => {
      showSnackbar({
        message:
          e?.response?.data?.responseMessage ||
          e?.response?.data?.errorMessage ||
          "Something went wrong !",
        open: true,
        variant: "error",
        duration: 3000,
      });
    },
  });
  const onSubmit = (value: MasterFormDataType) => {
    save([
      {
        clinicId: Number(user?.clinicId),
        hpiCode: value.hpiCode,
        hpiComId: Number(value.hpiComponent?.hpiComId),
        hpiCreatedByDate: "",
        hpiCreatedById: Number(user?.userId),
        hpiId: data?.hpiId || 0,
        hpiName: value?.hpiName || "",
        hpiStatus: Number(value.hpiStatus),
      },
    ]);
  };

  const handleReset = () => {
    reset(initialFormData);
  };

  useEffect(() => {
    if (data) {
      reset({
        hpiCode: data.hpiCode,
        hpiComponent: {
          hpiComId: data.hpiComId,
          hpiComCode: data.hpiComCode,
          hpiComName: data.hpiComName,
        },
        hpiName: data.hpiName,
        hpiStatus: data.hpiStatus,
      });
    }
  }, [data]);

  return (
    <div className="p-3">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Paper>
          <div className="flex justify-between items-center p-2">
            <div>
              <Typography variant="h6" className="p-3">
                {data ? "Update" : "Add"} {pageTitle}
              </Typography>
            </div>
            <div className="flex gap-2">
              <Button
                disabled={isLoading}
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleGoBack}
              >
                back
              </Button>
              {!Boolean(data) && (
                <Button
                  disabled={isLoading}
                  color="error"
                  type="reset"
                  onClick={handleReset}
                  startIcon={<CloseIcon />}
                >
                  reset
                </Button>
              )}
              <Button
                disabled={isLoading}
                type="submit"
                color="success"
                startIcon={
                  isLoading ? (
                    <CircularProgress size={20} color="success" />
                  ) : (
                    <SaveIcon />
                  )
                }
              >
                {data ? "update" : "save"}
              </Button>
            </div>
          </div>
          <Divider />
          <div className="p-3 grid grid-cols-4 gap-3">
            <CustomFormField
              control={control}
              name="hpiCode"
              element="input"
              fieldProps={{ label: "Enter HPI code " }}
              rules={{ required: "Code is required" }}
            />
            <CustomFormField
              control={control}
              name="hpiName"
              element="input"
              fieldProps={{ label: "Enter HPI name " }}
              rules={{ required: "Name is required" }}
            />
            <CustomFormField
              control={control}
              name="hpiComponent"
              loading={hpiComponentLoading}
              element="autocomplete"
              options={hpiComponent?.response || []}
              autocompleteProps={{
                getOptionLabel: (option) => option.hpiComName,
                isOptionEqualToValue: (option, value) =>
                  option.hpiComId == value.hpiComId,
              }}
              fieldProps={{ label: "Enter HPI component name" }}
              rules={{ required: "HPI component is required" }}
            />
            <CustomFormField
              control={control}
              name="hpiStatus"
              element="radio"
              radioProps={{ row: true }}
              radios={[
                { label: "Active", value: 1 },
                { label: "Inactive", value: 0 },
              ]}
              fieldProps={{ label: "Status " }}
              rules={{ required: "Status is required" }}
            />
          </div>
        </Paper>
      </form>
    </div>
  );
}

export default AddOrEditHPI;
